import { createReducer } from '@reduxjs/toolkit'
import flow from 'lodash/fp/flow'
import set from 'lodash/fp/set'
import update from 'lodash/fp/update'

import {
    changeCurrencyCode,
    dismissUserSurveySuccess,
    downloadBulkUpdate,
    downloadReport,
    fetchAutomationCapabilitiesSuccess,
    fetchCurrencySettingsSuccess,
    fetchCustomEventsSuccess,
    fetchHiddenAutomationCapabilities,
    fetchHomeDashboardIdSuccess,
    fetchOrganizationLabelsSuccess,
    fetchSovPermissionsSuccess,
    fetchTriggeredAlertsForDrawerFailure,
    fetchTriggeredAlertsForDrawerRequest,
    fetchTriggeredAlertsForDrawerSuccess,
    fetchUserSurveyLastReadDateSuccess,
    initiateUpdateSuccess,
    registerNewReport,
    mountAppFailure,
    mountAppRequest,
    mountAppSuccess,
    setGlobalNotification,
    setHiddenAutomationCapabilities,
    setHomeDashboardId,
    toggleReportsAppDrawer,
    toggleSidebarCollapsed,
    updateChartColorsMap,
} from 'actions/ui/app'
import { defaultCurrencyCode } from 'configuration/currency'
import { AsyncUpdate, ChartColorsMap, Report } from 'types'

import { addBrandGroupsCases } from './brandGroups'
import { defaultAppState } from './state'

export default createReducer(defaultAppState, (builder) => {
    builder
        .addCase(mountAppRequest, (state) => set(['mounting'], true)(state))
        .addCase(mountAppSuccess, (state) => set(['mounting'], false)(state))
        .addCase(mountAppFailure, (state) => set(['mounting'], false)(state))
        .addCase(changeCurrencyCode, (state, action) =>
            set(['currencyCode'], action.payload.currencyCode, state)
        )
        .addCase(fetchCurrencySettingsSuccess, (state, action) =>
            set(['currencyCode'], action.payload || defaultCurrencyCode, state)
        )
        .addCase(setHiddenAutomationCapabilities, (state, action) =>
            set(['hiddenAutomationCapabilities'], action.payload, state)
        )
        .addCase(fetchHiddenAutomationCapabilities, (state, action) =>
            set(['hiddenAutomationCapabilities'], action.payload, state)
        )
        .addCase(setGlobalNotification, (state, action) =>
            set(['globalNotification'], action.payload, state)
        )
        .addCase(fetchOrganizationLabelsSuccess, (state, action) =>
            set(['organizationLabels'], action.payload.results, state)
        )
        .addCase(fetchAutomationCapabilitiesSuccess, (state, action) =>
            set(['automationCapabilities'], action.payload.results, state)
        )
        .addCase(registerNewReport, (state, action) => {
            const report = action.payload
            if (!report?.id) {
                return state
            }
            if (
                state.newReports.some((existing) => existing.id === report.id)
            ) {
                return state
            }
            return set('newReports', [...state.newReports, report], state)
        })
        .addCase(downloadReport, (state, action) => {
            const { reportId } = action.payload
            return update(['newReports'], (newReports: Report[]) =>
                newReports.filter((report) => report.id !== reportId)
            )(state)
        })
        .addCase(initiateUpdateSuccess, (state, action) =>
            set('newUpdates', [...state.newUpdates, action.payload], state)
        )
        .addCase(downloadBulkUpdate, (state, action) => {
            const { id } = action.payload
            return update(['newUpdates'], (newUpdates: AsyncUpdate[]) =>
                newUpdates.filter((each) => each.id !== id)
            )(state)
        })
        .addCase(fetchTriggeredAlertsForDrawerRequest, (state) =>
            set(['triggeredAlerts', 'loading'], true, state)
        )
        .addCase(fetchTriggeredAlertsForDrawerSuccess, (state, action) => {
            const { results } = action.payload
            return flow(
                set(['triggeredAlerts', 'loading'], false),
                set(['triggeredAlerts', 'error'], null),
                set(['triggeredAlerts', 'results'], results ?? [])
            )(state)
        })
        .addCase(fetchTriggeredAlertsForDrawerFailure, (state, action) => {
            const message =
                (action.payload as { message?: string } | null)?.message ?? null
            return flow(
                set(['triggeredAlerts', 'loading'], false),
                set(['triggeredAlerts', 'error'], message)
            )(state)
        })
        .addCase(toggleReportsAppDrawer, (state, action) =>
            set(['drawerReports', 'isVisible'], action.payload, state)
        )
        .addCase(toggleSidebarCollapsed, (state) =>
            update(
                ['layout', 'isSidebarCollapsed'],
                (collapsed) => !collapsed
            )(state)
        )
        .addCase(fetchHomeDashboardIdSuccess, (state, action) =>
            set(['homeDashboardId'], action.payload)(state)
        )
        .addCase(setHomeDashboardId, (state, action) =>
            set(['homeDashboardId'], action.payload.dashboardId)(state)
        )
        .addCase(fetchCustomEventsSuccess, (state, action) =>
            set(['customEvents'], action.payload.results, state)
        )
        .addCase(fetchUserSurveyLastReadDateSuccess, (state, action) =>
            set(['userSurveyLastRead'], action.payload, state)
        )
        .addCase(dismissUserSurveySuccess, (state, action) =>
            set(['userSurveyLastRead'], action.payload, state)
        )
        .addCase(fetchSovPermissionsSuccess, (state, action) =>
            set(['sovPermissions'], action.payload)(state)
        )
        .addCase(updateChartColorsMap, (state, action) => {
            const newColorsMap: ChartColorsMap = {
                ...state.chartColorsMap,
                ...action.payload,
            }

            const MAX_ENTRIES = 5000
            const entries = Object.entries(newColorsMap)
            if (entries.length <= MAX_ENTRIES) {
                state.chartColorsMap = newColorsMap
                return
            }

            // Over the cap: drop the oldest 100 entries by updatedAt.
            const sortedEntries = entries.toSorted(
                ([, a], [, b]) => a.updatedAt - b.updatedAt
            )
            state.chartColorsMap = Object.fromEntries(sortedEntries.slice(100))
        })

    addBrandGroupsCases(builder)
})
